import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { BsBoxArrowUpRight, BsX } from "react-icons/bs";

type ResumePreviewModalProps = {
  src: string;
  title: string;
  onClose: () => void;
};

const ResumePreviewModal = ({ src, title, onClose }: ResumePreviewModalProps) => {
  const encodedSrc = encodeURI(src);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-label="Resume preview"
      onClick={onClose}
    >
      <PreviewPanel onClick={(e) => e.stopPropagation()}>
        <Toolbar>
          <Title>{title}</Title>
          <ToolbarActions>
            <OpenLink
              href={encodedSrc}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
              <BsBoxArrowUpRight aria-hidden />
            </OpenLink>
            <CloseButton type="button" aria-label="Close resume preview" onClick={onClose}>
              <BsX />
            </CloseButton>
          </ToolbarActions>
        </Toolbar>

        <PdfFrame
          src={encodedSrc}
          title={title}
        />
      </PreviewPanel>
    </Backdrop>,
    document.body
  );
};

export default ResumePreviewModal;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: 1100;
  display: flex;
  align-items: stretch;
  justify-content: center;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background: var(--resume-modal-backdrop);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  @media (min-width: 769px) {
    align-items: center;
    padding: 1.5rem;
  }
`;

const PreviewPanel = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--main-color);
  overflow: hidden;

  @media (min-width: 769px) {
    flex: 0 1 auto;
    width: min(96vw, 900px);
    height: min(92dvh, 820px);
    border: 1px solid var(--white-dart-light);
    border-radius: 6px;
    box-shadow: 0 12px 40px var(--shadow-medium);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--white-dart-light);
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--white-dart);
  letter-spacing: 0.02em;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const OpenLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--green);
  text-decoration: none;
  transition: var(--transition);

  &:hover {
    color: var(--white-dart);
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--white-dart);
  font-size: 1.75rem;
  line-height: 1;
  padding: 0.15rem;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    color: var(--green);
  }
`;

const PdfFrame = styled.iframe`
  flex: 1;
  width: 100%;
  min-height: 0;
  border: none;
  background: var(--pdf-frame-bg);
`;
